/**
 * Today — WO → SERVICE. Each work order is a card; its services are the
 * rows, each with the manager's description. Nothing is shown as a step:
 * the backend still keeps one task under every service, and the buttons
 * act on it through the service id, but the technician reads and
 * works services (2026-09-03, tasks per service retired).
 *
 * Three groups: "Deine Aufträge" (dispatched to me or taken by me), "Offene
 * Aufträge" (agreed work nobody holds — take the whole job), and the rest
 * (a colleague's job I share a step with). Fully-done jobs drop to DONE
 * TODAY.
 *
 * State is shown by ROW TREATMENT + control (never a colored status dot):
 *   - my running service  → green tile, white text, "YOU · RUNNING" + Pause
 *   - someone else's       → light tile, "<name> is on it" + a time tag
 *   - my paused service    → dashed-green tile, "yours · <t> logged" + Resume
 *   - mine, not started    → dashed-green tile + Start
 *   - open (nobody's)      → light tile, muted "open"; the CARD offers
 *                             "Auftrag übernehmen" for the whole job
 *   - awaiting approval    → greyed tile, phone note + dashed "APPROVAL" tag
 *   - done                 → folded into the card footer ("✓ n done — …")
 * The card holding my running service gets a green 2px border — that (not
 * a badge) is the "active card" marker.
 *
 * Header carries vehicle + who owns the job — NEVER a customer name — and,
 * when the job has a note nobody here has read yet, `NewNoteBadge` next to
 * the vehicle name (2026-09-22). Same on the flat DONE/UPCOMING rows
 * (`FlatWoRow`). Opening the job marks it read (WorkOrderDetailRoute).
 */
import { useRef, type KeyboardEvent } from 'react'
import { ArrowRight, Pause, Play, Phone } from '@phosphor-icons/react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { ButtonSpinner, NewNoteBadge, Plate, TimeBudget, type TimeBudgetState } from '@vollgas/shared/ui'
import { cn, compactPlate, formatDateLong, formatDuration } from '@vollgas/shared/utils'
import { FAST_TICK, SLOW_TICK, useNow } from '@vollgas/shared/hooks'
import { ClaimConfirmButton } from '@/components/ClaimConfirmButton'
import type { DoListService, DoListWorkOrder, WorkOrderResponse } from '@/apis/types'
import { useCurrentUser } from '@/auth/SessionContext'
import { hasUnreadNote, useNotesSeenAt, useNotesSeenReady } from '@/features/work-order/notesSeenStore'
import {
  useClaimService,
  useClaimWorkOrder,
  useDoList,
  useMyWorkOrders,
  usePauseTask,
  useStartTask,
} from '@/hooks/useWorkshop'
import {
  DONE_LIMIT,
  done as doneFilter,
  feedFor,
  inProgress,
  parseFilter,
  undispatched,
  upcoming,
} from './boardFilters'
import { useFlipMove } from '@/hooks/useFlipMove'
import { useActionToast } from '@/components/ActionToast'
import { useI18n } from '@/i18n/TranslationContext'
import { noteAuthorLabel } from '@/lib/format'
import { serviceView, serviceViewMinutes, type ServiceView } from '@/features/work-order/serviceView'
import { timerState, totalMinutes } from '@/features/work-order/woDetail'
import {
  canOpen,
  countableServices,
  diffTransitions,
  groupWorkOrders,
  hasMyRunning,
  hasStake,
  isClaimable,
  isServiceDone,
  serviceStateSnapshot,
  NO_TRANSITIONS,
  type TodaySnapshot,
  type TodayTransitions,
} from './todayGroups'

/** Base service-tile shape; the running tile overrides bg + text. */
const TILE = 'flex min-h-[3.25rem] items-start justify-between gap-3 rounded-sm px-3 py-2'
const LIGHT_TILE = `${TILE} border border-glass-border bg-glass-fill-strong`

/** Section label over a group of WO cards — matches the DONE TODAY header. */
const GROUP_LABEL = 'mb-2 text-11 font-bold uppercase tracking-mk-up12 text-glass-text-muted'

/** Stable empty feed, so the transition memo's identity check holds while
 *  the query is still resolving. */
const NO_WORK_ORDERS: readonly DoListWorkOrder[] = []

/**
 * What just changed since the last feed, for the two animations to key off.
 *
 * Keyed on the `workOrders` reference rather than recomputed every render:
 * react-query's structural sharing hands back the SAME array when a refetch
 * brings identical data, so the 30s poll produces no transitions and nothing
 * replays. Caching on that identity also makes this idempotent under
 * StrictMode's double render — recomputing there would diff the snapshot
 * against itself and swallow the animation.
 */
function useTodayTransitions(workOrders: readonly DoListWorkOrder[], meId: string | null): TodayTransitions {
  const memo = useRef<{ source: readonly DoListWorkOrder[] | null; prev: TodaySnapshot | null; transitions: TodayTransitions }>({
    source: null,
    prev: null,
    transitions: NO_TRANSITIONS,
  })

  if (memo.current.source !== workOrders) {
    memo.current = {
      source: workOrders,
      prev: serviceStateSnapshot(workOrders, meId),
      transitions: diffTransitions(memo.current.prev, workOrders, meId),
    }
  }
  return memo.current.transitions
}

export function TodayRoute() {
  const { t, lang } = useI18n()
  const meId = useCurrentUser()?.id ?? null
  const [params] = useSearchParams()
  const filter = parseFilter(params.get('filter'))
  const flat = feedFor(filter) === 'flat'
  const { data, isLoading, isError } = useDoList()
  // Only DONE and UPCOMING read the second feed, and only then is it
  // fetched — the default board must not pay for a request it ignores.
  const mine = useMyWorkOrders(flat)
  const claimJob = useClaimWorkOrder()
  const claimService = useClaimService()
  const start = useStartTask()
  const pause = usePauseTask()
  const toast = useActionToast()
  // Only a live timer needs the fast tick — the feed's other minute figures
  // are settled totals. With the optimistic patch this flips on tap, so
  // Resume restarts a visibly moving clock rather than a frozen one.
  const now = useNow(data?.workOrders.some(hasMyRunning) ? FAST_TICK : SLOW_TICK)
  const transitions = useTodayTransitions(data?.workOrders ?? NO_WORK_ORDERS, meId)
  // Cards slide between ranks instead of teleporting when the board re-sorts.
  const flip = useFlipMove()

  // The flat tabs wait on their own feed, the tree tabs on the do-list.
  const loading = flat ? mine.isLoading : isLoading
  const failed = flat ? mine.isError : isError || !data
  if (loading) return <div role="status" className="py-12 text-center text-sm text-glass-text-muted">{t.home.loading}</div>
  if (failed) return <div role="alert" className="py-12 text-center text-sm text-glass-text-muted">{t.home.errorPanel}</div>

  const feed = data?.workOrders ?? NO_WORK_ORDERS
  const flatRows = mine.data ?? []

  // ── the two flat tabs: history and the plan ──────────────────────────
  if (filter === 'done' || filter === 'upcoming') {
    const rows = filter === 'done' ? doneFilter(flatRows) : upcoming(flatRows)
    const emptyText =
      filter === 'done' ? t.filters.emptyDone : t.filters.emptyUpcoming
    return (
      <section aria-label={t.home.regionLabel}>
        <FilterHeading title={t.filters[filter]} lang={lang} />
        {rows.length === 0 ? (
          <p className="py-12 text-center text-sm text-glass-text-muted">{emptyText}</p>
        ) : (
          <>
            <div className="flex flex-col gap-2">
              {rows.map((wo) => (
                <FlatWoRow key={wo.id} wo={wo} kind={filter} lang={lang} meId={meId} />
              ))}
            </div>
            {filter === 'done' && rows.length === DONE_LIMIT && (
              <p className="mt-3 text-center text-11-5 text-glass-text-muted">
                {t.filters.doneCapped}
              </p>
            )}
          </>
        )}
      </section>
    )
  }

  // ── the three tree tabs ──────────────────────────────────────────────
  const grouped = groupWorkOrders(feed, meId)
  const { pool, rest, done: doneToday } = grouped
  const myCards = grouped.mine
  const sliced =
    filter === 'undispatched'
      ? undispatched(feed, meId)
      : filter === 'progress'
        ? inProgress(feed)
        : null

  const empty = sliced ? sliced.length === 0 : feed.length === 0
  const groups = sliced
    ? [{ key: filter, label: '', cards: sliced }]
    : [
        { key: 'mine', label: t.home.yourWork, cards: myCards },
        { key: 'pool', label: t.home.openJobs, cards: pool },
        { key: 'rest', label: t.home.otherWork, cards: rest },
      ].filter((g) => g.cards.length > 0)
  // Labels earn their place: one group is one unlabelled list of cards.
  const labelled = groups.length > 1

  // Taking the whole job off the floor. A lost race (a colleague got there
  // first, 409) still invalidates the do-list (hook's onSettled), so the
  // stale card drops on refetch instead of lingering as claimable. The
  // toast says why — the server writes that sentence.
  const takeJob = (workOrderId: string) => {
    toast.clear()
    claimJob.mutate(workOrderId, {
      onError: (error) => toast.reportError(error, () => takeJob(workOrderId)),
    })
  }
  // Taking ONE loose service, once the tech already holds a stake elsewhere
  // on the job — the per-service counterpart to takeJob above. Same lost-race
  // handling: onSettled still refetches so a service somebody else took a
  // beat earlier drops off instead of lingering as offered.
  const takeService = (workOrderId: string, serviceId: string) => {
    toast.clear()
    claimService.mutate({ workOrderId, serviceId }, {
      onError: (error) => toast.reportError(error, () => takeService(workOrderId, serviceId)),
    })
  }
  // Start and Resume are the same backend action ("begin timing now") —
  // the service's OWN state (claimed vs paused) is what makes the button
  // read "Start" or "Resume".
  //
  // Both are optimistic, so a failure is a row that already moved and then
  // moves back. Reporting it is what separates that from a glitch.
  const startTask = (serviceId: string) => {
    toast.clear()
    start.mutate(serviceId, { onError: (error) => toast.reportError(error, () => startTask(serviceId)) })
  }
  const pauseTask = (serviceId: string) => {
    toast.clear()
    pause.mutate(serviceId, { onError: (error) => toast.reportError(error, () => pauseTask(serviceId)) })
  }
  // ONE flag, not one per direction. The optimistic patch flips the row the
  // instant it is tapped, so a paused row tapped Resume re-renders as a
  // running row — and the Resume button holding the spinner is unmounted
  // before the request resolves. Scoping the flag to the TASK lets the button
  // that replaces it carry the spinner through, so the icon settling into
  // Play/Pause is what says the server agreed.
  const timerServiceId = start.isPending
    ? start.variables ?? null
    : pause.isPending
      ? pause.variables ?? null
      : null

  return (
    <section aria-label={t.home.regionLabel}>
      {sliced ? (
        <FilterHeading title={t.filters[filter]} lang={lang} />
      ) : (
        <div className="mb-5">
          <h1 className="font-display text-20 font-extrabold tracking-mk-neg2 text-glass-text">
            {formatDateLong(new Date().toISOString(), lang)}
          </h1>
        </div>
      )}

      {empty ? (
        sliced ? (
          <p className="py-12 text-center text-sm text-glass-text-muted">
            {filter === 'undispatched'
              ? t.filters.emptyUndispatched
              : t.filters.emptyProgress}
          </p>
        ) : (
          <EmptyState />
        )
      ) : (
        <>
          {groups.map((group) => (
            <div key={group.key} className="mb-5 last:mb-0">
              {labelled && <div className={GROUP_LABEL}>{group.label}</div>}
              <div className="flex flex-col gap-3">
                {group.cards.map((wo) => (
                  <WoCard
                    key={wo.id}
                    wo={wo}
                    meId={meId}
                    now={now}
                    innerRef={flip.register(wo.id)}
                    arrived={transitions.arrivedWoIds.has(wo.id)}
                    startedServiceIds={transitions.startedServiceIds}
                    onTakeJob={takeJob}
                    claimingJob={claimJob.isPending && claimJob.variables === wo.id}
                    onTakeService={takeService}
                    claimingServiceId={claimService.isPending ? claimService.variables?.serviceId ?? null : null}
                    onStart={startTask}
                    onPause={pauseTask}
                    timerServiceId={timerServiceId}
                  />
                ))}
              </div>
            </div>
          ))}

          {!sliced && doneToday.length > 0 && (
            <>
              <div className="mb-2 mt-5 text-11 font-bold uppercase tracking-mk-up12 text-glass-text-muted">
                {t.home.doneToday(doneToday.length)}
              </div>
              <div className="flex flex-col gap-2">
                {doneToday.map((wo) => (
                  <DoneReceipt key={wo.id} wo={wo} now={now} />
                ))}
              </div>
            </>
          )}
        </>
      )}
    </section>
  )
}

interface WoCardProps {
  readonly wo: DoListWorkOrder
  readonly meId: string | null
  readonly now: number
  /** From `useFlipMove` — lets the card slide from its previous position
   *  when the board re-sorts around it. */
  readonly innerRef: (node: HTMLDivElement | null) => void
  /** This card just moved up into "your jobs" — play its entrance. Suppressed
   *  by the FLIP for any card that had a previous position to travel from. */
  readonly arrived: boolean
  /** Tasks whose timer just began — the green fills rather than snaps. */
  readonly startedServiceIds: ReadonlySet<string>
  /** Take the whole job — every loose service on it. Offered only while the
   *  tech holds no stake elsewhere on the job; see `isClaimable`. */
  readonly onTakeJob: (workOrderId: string) => void
  readonly claimingJob: boolean
  /** Take ONE loose service — offered per row once the tech already holds a
   *  stake elsewhere on the job. */
  readonly onTakeService: (workOrderId: string, serviceId: string) => void
  /** The service with a per-service claim request in flight, if any. */
  readonly claimingServiceId: string | null
  readonly onStart: (serviceId: string) => void
  readonly onPause: (serviceId: string) => void
  /** The task with a timer request in flight, if any — either direction. */
  readonly timerServiceId: string | null
}

/**
 * A car with no make/model/year on file has the backend fall back
 * `vehicleDisplayName` to the raw plate. Rendered next to the formatted
 * `<Plate>` badge that reads as the same plate twice in two spellings
 * ("T-76672" badge, "T76672" title) — treat a plate-shaped display name
 * as absent instead. Compared compact (strip separators, upper-case) so
 * a raw vs. formatted spelling of the same plate is still caught.
 */
function vehicleTitle(plate: string | null | undefined, display: string | null | undefined): string | null {
  if (!display) return null
  if (plate && compactPlate(display) === compactPlate(plate)) return null
  return display
}

function WoCard({ wo, meId, now, innerRef, arrived, startedServiceIds, onTakeJob, claimingJob, onTakeService, claimingServiceId, onStart, onPause, timerServiceId }: WoCardProps) {
  const navigate = useNavigate()
  const { t } = useI18n()
  const open = () => navigate(`/work-orders/${wo.id}`)
  // A note nobody on this job has read yet — so a mechanic sees it without
  // opening the job. Anonymous (session still resolving) reads as unread,
  // the honest answer when we cannot tell what has been read.
  const seenAt = useNotesSeenAt(meId)
  const seenReady = useNotesSeenReady(meId)
  const unread = seenReady && hasUnreadNote(
    seenAt,
    wo.id,
    wo.lastNoteAt ? new Date(wo.lastNoteAt) : null,
    wo.lastNoteAuthorUserId,
    meId,
  )

  // The WO detail is ownership-scoped server-side: the caller must own the
  // job or hold a step on it. A card with no stake of mine has no page to
  // open — it used to navigate anyway and land on "Could not load work
  // order." No stake, no affordance: not a button, not focusable, no pointer.
  const openable = canOpen(wo, meId)
  // Same fact as `openable` (canOpen === hasStake), named for what it drives
  // here: once true, a loose row offers its OWN "Take service" instead of
  // the card offering "Take job" for the whole thing.
  const stake = hasStake(wo, meId)
  const active = hasMyRunning(wo)
  const views = wo.services.map((svc) => ({ svc, view: serviceView(svc) }))
  const countable = countableServices(wo)
  const total = countable.length
  const doneRows = countable.filter(isServiceDone)
  const doneCount = doneRows.length
  // What the ESTIMATE is measured against: every mechanic's time on the
  // job, not the reader's share of it. See `totalMinutes` in woDetail.
  const jobMinutes = totalMinutes(wo.services, now)
  const claimable = isClaimable(wo, meId)
  const numbered = wo.services.length > 1
  // The job estimate is the sum of the SERVICES' estimates.
  const estMinutes = wo.services.reduce((s, svc) => s + svc.durationMinutes, 0)
  const owner = wo.assignedTechnicianName === null
    ? null
    : (wo.assignedTechnicianId === meId ? t.tasks.you : wo.assignedTechnicianName)
  // The advisor's latest word on the job — the rest sits on the job page
  // and in the claim dialog.
  const lastNote = wo.notes.length > 0 ? wo.notes[wo.notes.length - 1]! : null

  return (
    <div
      ref={innerRef}
      {...(openable
        ? {
            role: 'button' as const,
            tabIndex: 0,
            onClick: open,
            onKeyDown: (e: KeyboardEvent<HTMLDivElement>) => { if (e.key === 'Enter') open() },
          }
        : {})}
      className={cn(
        'rounded-md border bg-glass-fill p-px18 text-left text-glass-text transition-colors',
        openable && 'cursor-pointer hover:bg-glass-fill-strong',
        active ? 'border-2 border-green shadow-md' : 'border-glass-border',
        arrived && 'motion-safe:animate-row-in motion-reduce:animate-fade-in',
      )}
    >
      {/* Header — vehicle + who owns the job + state summary */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex min-w-0 flex-wrap items-center gap-2.5">
          {wo.vehiclePlate && <Plate text={wo.vehiclePlate} />}
          <span className="truncate font-display text-15-5 font-extrabold tracking-mk-neg2">{vehicleTitle(wo.vehiclePlate, wo.vehicleDisplayName) ?? ''}</span>
          {unread && <NewNoteBadge label={t.workOrder.newNoteBadge} />}
          {owner && (
            <span className="rounded bg-glass-fill-strong px-2 py-0.5 text-10-5 font-bold uppercase tracking-mk-up12 text-glass-text-muted">
              {owner}
            </span>
          )}
        </div>
        <HeaderMeta
          jobMinutes={jobMinutes}
          estMinutes={estMinutes}
          state={timerState(wo.services)}
        />
      </div>

      {/* The advisor's note on the job — what the customer said. */}
      {lastNote && (
        <p className="mt-1.5 line-clamp-2 text-12-5 text-glass-text-muted">
          <span className="font-semibold">{noteAuthorLabel(lastNote, t.tasks.you)} · </span>
          {lastNote.body}
          {wo.notes.length > 1 && <span className="font-semibold"> +{wo.notes.length - 1}</span>}
        </p>
      )}

      {/* Services — a job dispatched with none yet (2026-09-22) shows the
          muted placeholder instead of an empty list; no per-service rows,
          no "Take job" (isClaimable is false with no loose service). */}
      {wo.services.length === 0 ? (
        <p className="mt-2 text-12-5 text-glass-text-muted">{t.tasks.noServiceYet}</p>
      ) : (
        <div className="mt-2 flex flex-col gap-1.5">
          {views
            .filter(({ view }) => view.state !== 'done')
            .map(({ svc, view }) => (
              <ServiceRow
                key={svc.id}
                service={svc}
                view={view}
                label={numbered ? `${t.tasks.service} ${svc.index}/${svc.total} · ${svc.name}` : svc.name}
                now={now}
                justStarted={startedServiceIds.has(svc.id)}
                onStart={onStart}
                onPause={onPause}
                timerPending={timerServiceId === svc.id}
                stake={stake}
                onTakeService={(serviceId) => onTakeService(wo.id, serviceId)}
                takingService={claimingServiceId === svc.id}
              />
            ))}
        </div>
      )}

      {/* Footer — take the job / done summary / open */}
      {(claimable || doneCount > 0 || openable) && (
        <div className="mt-2 flex flex-wrap items-center justify-between gap-3 border-t border-glass-divider pt-2 text-11-5 text-glass-text-muted">
          <span className="min-w-0 truncate">
            {doneCount > 0 && <>✓ {t.tasks.doneCount(doneCount)} — {doneRows.map((svc) => svc.name).join(' · ')}</>}
          </span>
          {claimable ? (
            <ClaimConfirmButton
              onConfirm={() => onTakeJob(wo.id)}
              seededNotes={wo.notes}
              onClickCapture={(e) => e.stopPropagation()}
              disabled={claimingJob}
              className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-full border border-glass-border bg-transparent px-4 py-2 text-13 font-bold text-glass-text transition hover:bg-glass-text hover:text-white active:scale-[0.97] disabled:opacity-60"
            >
              {claimingJob && <ButtonSpinner />}
              {t.tasks.claimJob}
              {!claimingJob && <ArrowRight size={13} weight="bold" aria-hidden="true" />}
            </ClaimConfirmButton>
          ) : openable ? (
            <span className="flex flex-shrink-0 items-center gap-1 font-semibold">
              {t.home.openWO} <ArrowRight size={11} weight="bold" aria-hidden="true" />
            </span>
          ) : null}
        </div>
      )}
    </div>
  )
}

interface HeaderMetaProps {
  readonly jobMinutes: number
  readonly estMinutes: number
  readonly state: TimeBudgetState
}

/**
 * The card's right-hand summary: how much of the job is done, and how
 * much of its time is left.
 *
 * <p>This used to be a three-way branch that showed the estimate OR the
 * reader's own time OR neither, and never two of them — so the card
 * printed "gesch. 1h 15m" until somebody started a clock and "deine Zeit
 * 52m" from then on. The one number a technician is actually deciding
 * on, how much of the estimate is left, was never on the card at all and
 * could not be worked out from it, because the half it showed changed
 * depending on which half you needed.
 */
function HeaderMeta({ jobMinutes, estMinutes, state }: HeaderMetaProps) {
  const { t } = useI18n()
  return (
    <div className="flex flex-shrink-0 flex-col items-end gap-0.5">
      {/* An unsized job (the catalogue stopped carrying durations, so its
          estimate is 0 rather than missing) renders the spend alone — the
          budget knows to drop the figure it has no basis for. Nothing at
          all until somebody has either sized it or worked it, so a fresh
          card stays as quiet as it was. */}
      {(estMinutes > 0 || jobMinutes > 0) && (
        <TimeBudget
          className="items-end text-right"
          agreedMinutes={estMinutes}
          workedMinutes={jobMinutes}
          state={state}
          labels={t.tasks.budget}
        />
      )}
    </div>
  )
}

interface ServiceRowProps {
  readonly service: DoListService
  readonly view: ServiceView
  readonly label: string
  readonly now: number
  /** The timer just started on this service — wipe the green in from the
   *  left instead of snapping. False on every subsequent render, so a
   *  background refetch never replays it. */
  readonly justStarted: boolean
  readonly onStart: (serviceId: string) => void
  readonly onPause: (serviceId: string) => void
  /** A start or pause for THIS service is in flight — the spinner stands in
   *  for whichever icon the row is currently showing until the server answers. */
  readonly timerPending: boolean
  /** The signed-in tech already holds a stake elsewhere on this job — a
   *  loose row offers its own "Take service" instead of "Open" alone. */
  readonly stake: boolean
  readonly onTakeService: (serviceId: string) => void
  /** A per-service claim on THIS row is in flight. */
  readonly takingService: boolean
}

/** The manager's description under the service name — what to do. */
function Description({ text, muted }: { readonly text: string | null; readonly muted?: boolean }) {
  if (!text) return null
  return <div className={cn('mt-0.5 line-clamp-2 text-12 text-glass-text-muted', muted && 'opacity-80')}>{text}</div>
}

function ServiceRow({ service, view, label, now, justStarted, onStart, onPause, timerPending, stake, onTakeService, takingService }: ServiceRowProps) {
  const { t } = useI18n()
  const stop = (e: React.MouseEvent) => e.stopPropagation()
  const minutes = formatDuration(serviceViewMinutes(view, now), { days: true })

  // My running service — green tile, white text. At rest this is exactly the
  // flat green it always was; on start a shade retreats left-to-right so
  // the green reads as filling in.
  if (view.state === 'in_progress' && view.mine) {
    return (
      <div className={cn(TILE, 'relative items-center overflow-hidden bg-green text-white')}>
        {justStarted && (
          <span
            aria-hidden="true"
            className="absolute inset-0 origin-right bg-black/25 motion-safe:animate-tile-fill motion-reduce:hidden"
          />
        )}
        <div className="relative min-w-0">
          <div className="truncate text-13-5 font-bold">{label}</div>
          {service.description && <div className="mt-0.5 line-clamp-2 text-12 text-white/80">{service.description}</div>}
        </div>
        <div className="relative flex flex-shrink-0 items-center gap-2">
          <span className="rounded-full border border-white/40 px-2 py-0.5 text-10-5 font-bold uppercase tracking-mini">{t.tasks.you} · {t.tasks.running}</span>
          <span className="font-display text-15 font-extrabold tracking-mk-neg2">{minutes}</span>
          {(
            <button
              type="button"
              onClick={(e) => { stop(e); onPause(service.id) }}
              disabled={timerPending}
              className="inline-flex items-center gap-1.5 rounded-full bg-orange px-4 py-2 text-13 font-bold text-white transition hover:opacity-90 disabled:opacity-60"
            >
              {timerPending ? <ButtonSpinner /> : <Pause size={13} weight="fill" aria-hidden="true" />} {t.tasks.pause}
            </button>
          )}
        </div>
      </div>
    )
  }

  // Someone else's running service — light tile, name as text (no avatar).
  if (view.state === 'in_progress') {
    return (
      <div className={LIGHT_TILE}>
        <div className="min-w-0">
          <div className="truncate text-13-5 font-bold text-glass-text">{label}</div>
          <Description text={service.description} />
        </div>
        <div className="flex flex-shrink-0 items-center gap-2">
          <span className="text-12-5 text-glass-text-muted"><span className="font-semibold text-glass-text">{view.mechanicName}</span> {t.tasks.onIt}</span>
          <span className="rounded bg-glass-text px-1.5 py-0.5 text-10-5 font-bold text-white">{minutes}</span>
        </div>
      </div>
    )
  }

  // Held by someone else, not running — read-only, all muted, no tag.
  if ((view.state === 'claimed' || view.state === 'paused') && !view.mine) {
    return (
      <div className={LIGHT_TILE}>
        <div className="min-w-0">
          <div className="truncate text-13-5 font-bold text-glass-text">{label}</div>
          <Description text={service.description} />
        </div>
        <span className="flex-shrink-0 text-12-5 text-glass-text-muted">
          {view.mechanicName} · {view.state === 'paused' ? `${t.workOrder.timerPaused} · ${minutes}` : t.tasks.claimedNotStarted}
        </span>
      </div>
    )
  }

  // Mine, not started yet — explicit Start.
  if (view.state === 'claimed' && view.mine) {
    return (
      <div className={cn(TILE, 'border-2 border-dashed border-green bg-glass-fill-strong')}>
        <div className="min-w-0">
          <div className="truncate text-13-5 font-bold text-glass-text">{label}</div>
          <Description text={service.description} />
        </div>
        {(
          <button
            type="button"
            onClick={(e) => { stop(e); onStart(service.id) }}
            disabled={timerPending}
            className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-full bg-green px-4 py-2 text-13 font-bold text-white transition hover:opacity-90 active:scale-[0.97] disabled:opacity-60"
          >
            {timerPending ? <ButtonSpinner /> : <Play size={13} weight="fill" aria-hidden="true" />} {t.tasks.start}
          </button>
        )}
      </div>
    )
  }

  // My paused service — dashed-green tile + Note (outlined) + Resume (solid).
  if (view.state === 'paused' && view.mine) {
    return (
      <div className={cn(TILE, 'border-2 border-dashed border-green bg-glass-fill-strong')}>
        <div className="min-w-0">
          <div className="truncate text-13-5 font-bold text-glass-text">{label}</div>
          <Description text={service.description} />
          <div className="mt-0.5 text-11-5 text-glass-text-muted">{t.tasks.yoursLogged(minutes)}</div>
        </div>
        {(
          <button
            type="button"
            onClick={(e) => { stop(e); onStart(service.id) }}
            disabled={timerPending}
            className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-full bg-green px-4 py-2 text-13 font-bold text-white transition hover:opacity-90 active:scale-[0.97] disabled:opacity-60"
          >
            {timerPending ? <ButtonSpinner /> : <Play size={13} weight="fill" aria-hidden="true" />} {t.tasks.resume}
          </button>
        )}
      </div>
    )
  }

  // Awaiting approval — greyed, phone note, dashed APPROVAL tag.
  if (view.state === 'awaiting_approval') {
    return (
      <div className={cn(LIGHT_TILE, 'opacity-[0.7]')}>
        <div className="min-w-0">
          <div className="truncate text-13-5 font-bold text-glass-text-muted">{label}</div>
          <Description text={service.description} muted />
          <div className="mt-0.5 flex items-center gap-1 text-11-5 text-glass-text-muted">
            <Phone size={11} weight="fill" aria-hidden="true" /> {t.tasks.awaitingCustomer}
          </div>
        </div>
        <span className="flex-shrink-0 rounded border border-dashed border-glass-border px-2 py-0.5 text-10-5 font-bold uppercase tracking-mk-up12 text-glass-text-muted">
          {t.tasks.approval}
        </span>
      </div>
    )
  }

  // Open — nobody holds it. Before the tech has any stake on this job, the
  // claim is the CARD's "Auftrag übernehmen"; once they do, this row offers
  // its OWN "Take service" instead — see `stake`.
  return (
    <div className={LIGHT_TILE}>
      <div className="min-w-0">
        <div className="truncate text-13-5 font-bold text-glass-text">{label}</div>
        <Description text={service.description} />
      </div>
      {stake ? (
        <button
          type="button"
          onClick={(e) => { stop(e); onTakeService(service.id) }}
          disabled={takingService}
          className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-full border border-glass-border bg-transparent px-4 py-2 text-13 font-bold text-glass-text transition hover:bg-glass-text hover:text-white active:scale-[0.97] disabled:opacity-60"
        >
          {takingService && <ButtonSpinner />}
          {t.tasks.takeService}
          {!takingService && <ArrowRight size={13} weight="bold" aria-hidden="true" />}
        </button>
      ) : (
        <span className="flex-shrink-0 text-12-5 text-glass-text-muted">{t.tasks.statusOpen}</span>
      )}
    </div>
  )
}

/** A fully-done WO — one muted, dashed receipt row in the DONE TODAY list. */
function DoneReceipt({ wo, now }: { readonly wo: DoListWorkOrder; readonly now: number }) {
  const navigate = useNavigate()
  const open = () => navigate(`/work-orders/${wo.id}`)
  const views = wo.services.map(serviceView)
  const minutes = views.reduce((s, v) => s + serviceViewMinutes(v, now), 0)
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={open}
      onKeyDown={(e) => { if (e.key === 'Enter') open() }}
      className="flex cursor-pointer flex-wrap items-center justify-between gap-2 rounded-md border border-dashed border-glass-border bg-glass-fill px-px18 py-3 text-glass-text opacity-[0.6] transition-opacity hover:opacity-100"
    >
      <div className="flex min-w-0 items-center gap-2.5">
        {wo.vehiclePlate && <Plate text={wo.vehiclePlate} />}
        <span className="truncate font-display text-15-5 font-extrabold tracking-mk-neg2">{vehicleTitle(wo.vehiclePlate, wo.vehicleDisplayName) ?? ''}</span>
      </div>
      <span className="flex items-center gap-2 text-12-5 font-semibold text-glass-text-muted">
        <span>✓ {views.length}/{views.length}</span>
        <span aria-hidden="true">·</span>
        <span>{formatDuration(minutes, { days: true })}</span>
      </span>
    </div>
  )
}

function EmptyState() {
  const { t } = useI18n()
  return (
    // Mockup `.work-empty` — vertical 26px, horizontal 22px (not symmetric).
    <div className="rounded-md border border-dashed border-glass-border bg-glass-fill py-px26 px-px22 text-center">
      <div className="mb-2 text-32 opacity-60" aria-hidden="true">☕</div>
      <div className="mb-1 text-15 font-bold text-glass-text">{t.home.emptyTitle}</div>
      <div className="text-12-5 text-glass-text-muted">{t.home.emptySub}</div>
    </div>
  )
}

/**
 * Heading for a filtered view. The board's own heading is today's date,
 * which is a lie on DONE (older work) and on UPCOMING (later work) — so a
 * slice says which slice it is instead.
 */
function FilterHeading({ title, lang }: { readonly title: string; readonly lang: 'de' | 'en' }) {
  void lang
  return (
    <div className="mb-5">
      <h1 className="font-display text-20 font-extrabold tracking-mk-neg2 text-glass-text">
        {title}
      </h1>
    </div>
  )
}

/**
 * A row on the flat feed — DONE and UPCOMING.
 *
 * Deliberately leaner than `WoCard`. `/technical/work-orders/active`
 * returns no service/task tree, so there is no per-step breakdown to
 * show, and inventing a card that looks like the board's would promise
 * detail that is not on the wire. Plate, vehicle, and the one timestamp
 * the tab is about.
 */
function FlatWoRow({
  wo,
  kind,
  lang,
  meId,
}: {
  readonly wo: WorkOrderResponse
  readonly kind: 'done' | 'upcoming'
  readonly lang: 'de' | 'en'
  readonly meId: string | null
}) {
  const navigate = useNavigate()
  const { t } = useI18n()
  const stamp = kind === 'done' ? wo.completedAt ?? wo.openedAt : wo.scheduledAt
  const label = kind === 'done' ? t.filters.finished : t.filters.scheduledFor
  const seenAt = useNotesSeenAt(meId)
  const seenReady = useNotesSeenReady(meId)
  const unread = seenReady && hasUnreadNote(
    seenAt,
    wo.id,
    wo.lastNoteAt ? new Date(wo.lastNoteAt) : null,
    wo.lastNoteAuthorUserId,
    meId,
  )
  return (
    <button
      type="button"
      onClick={() => navigate(`/work-orders/${wo.id}`)}
      className="flex w-full items-center gap-3 rounded-md border border-glass-border bg-glass-fill px-3 py-2.5 text-left transition hover:bg-glass-fill-strong"
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          {wo.vehiclePlate && <Plate text={wo.vehiclePlate} />}
          <span className="truncate text-13 font-semibold text-glass-text">
            {vehicleTitle(wo.vehiclePlate, wo.vehicleDisplayName) ?? wo.reference ?? wo.id}
          </span>
          {unread && <NewNoteBadge label={t.workOrder.newNoteBadge} />}
        </div>
        {stamp && (
          <div className="mt-0.5 text-11-5 text-glass-text-muted">
            {label} {formatDateLong(stamp, lang)}
          </div>
        )}
      </div>
      <ArrowRight size={16} weight="bold" className="shrink-0 text-glass-text-muted" />
    </button>
  )
}
